"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import {
  SearchIcon,
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useSettings } from '@/themes/core/hooks';

import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Command
} from '@/themes/core/ui/command';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/themes/core/ui/dialog';

export function Search({ placeholder }: { placeholder?: string }) {
  const [open, setOpen] = React.useState(false);
  const router = useRouter();
  const { settings } = useSettings();
  const headerConfig = (settings.header as any) || {};

  // Customization - Modern 02
  const quickLinksTitle = headerConfig.search_quick_links_title || "روابط سريعة";
  const quickLinks = (headerConfig.search_quick_links as any[]) || [
    { text: "جميع المنتجات", url: "/search", icon: "ShoppingBag" },
    { text: "وصل حديثاً", url: "/search?sort=latest", icon: "TrendingUp" },
    { text: "تخفيضات", url: "/search?q=sale", icon: "Tag" }
  ];

  // Resolve direction
  const dir = headerConfig.navbar_direction === 'ltr' ? 'ltr' : (headerConfig.navbar_direction === 'rtl' ? 'rtl' : undefined);

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const handleSelect = (path: string) => {
    setOpen(false);
    router.push(path);
  };

  return (
    <>
      <button className="flex items-center justify-center h-10 w-10 hover:bg-primary-foreground/10 rounded-full mb-0 transition-colors" onClick={() => setOpen(true)}>
        <SearchIcon
          className="text-primary-foreground"
          size={20}
          aria-hidden="true"
        />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`overflow-hidden p-0 [&>button]:!top-3 ${dir === 'ltr' ? '[&>button]:!right-4 [&>button]:!left-auto' : '[&>button]:!left-4 [&>button]:!right-auto'}`}>
          <DialogTitle className="sr-only">البحث</DialogTitle>
          <div dir={dir} className="w-full">
            <Command className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
              <CommandInput
                placeholder={headerConfig.search_primary_title || placeholder || "بحث..."}
                className="text-start"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    // @ts-ignore
                    const value = e.target.value;
                    if (value) {
                      setOpen(false);
                      router.push(`/search?q=${encodeURIComponent(value)}`);
                    }
                  }
                }}
              />
              <CommandList className="text-start max-h-[300px] overflow-y-auto">
                <CommandEmpty>لا توجد نتائج.</CommandEmpty>

                <CommandGroup heading={quickLinksTitle}>
                  {quickLinks.map((item, i) => {
                    // Resolve Icon
                    const IconName = item.icon || "Tag";
                    const IconComponent = (LucideIcons as any)[IconName] || LucideIcons.Tag;

                    return (
                      <CommandItem key={i} onSelect={() => handleSelect(item.url || '/')} className="cursor-pointer flex items-center gap-2 me-2">
                        <IconComponent size={16} className="opacity-60" />
                        <span>{item.text}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
